import { _HttpMethods, DayOfWeek } from '../constants/staticTypes';
import { _DAYJS as dayjs } from '../config/dayjs.config';
import isoWeek from 'dayjs/plugin/isoWeek';
import HttpServiceHelper from './httpServiceHelper';
import { scheduleApi, userRolesApi, usersApi, venueStaffOptionsApi } from '../constants/apiEndPoints';
import isBetween from 'dayjs/plugin/isBetween'; // Import the weekday plugin
import moment from 'moment';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Badge } from 'primereact/badge';
import { DATE_FORMAT } from '../constants/constants';
import CustomTooltip from '../pages/organization/venue/venue-schedule/components/CustomTooltip';
import weekday from 'dayjs/plugin/weekday'; // Import the weekday plugin

export type Holidays = {
    startDate: string; // "YYYY-MM-DD" format
    endDate: string; // "YYYY-MM-DD" format
    startTime: string | null;
    endTime: string | null;
    timeLabel: string | null;
    durationType: 'full-day' | 'half-day' | 'custom'; // assuming possible values based on context
    title: string;
    isOpenForBusiness: boolean;
    isPaid: boolean;
    isApproved: boolean;
    isPublished: boolean | null;
    actionStatus: 'draft' | 'pending' | 'completed' | 'cancelled'; // assuming possible values based on context
};

export type MandatoryDay = {
    id: string;
    tenantId: string;
    startDate: string;
    endDate: string;
    isApproved: boolean | null;
    isDraft: boolean | null;
    isPublished: boolean | null;
    timeLabel: string;
    durationType: string;
    title: string | null;
    actionStatus: string;
    createdAt: string;
    updatedAt: string;
    deletedAt: string | null;
    tenant_id: string;
};

export type JobRole = {
    id: string;
    jobRoleName: string;
    jobRoleDescription: string;
    isActive: boolean;
    createdAt: string;
    jobRoleId?: string;
    jobRoleDepartmentId: string;
    jobRoleDepartment: {
        id: string;
        departmentName: string;
        departmentDescription: null;
    };
    isAvailableForSchedule: boolean | null;
    styleDetails?: StyleDetails;
};

export type JobRoleForShift = {
    id: string;
    tenantStaffId: string;
    jobRoleId: string;
    effectiveFrom: string;
    effectiveEnd: string | null;
    isCurrent: boolean;
    createdAt: string;
    updatedAt: string;
    deletedAt: string | null;
    job_role_id: string;
    tenant_staff_id: string;
    jobRole: JobRole;
};

export interface Shift {
    departmentId: string;
    departmentName: string;
    date: string;
    day: DayOfWeek;
    role: string;
    roleId: string;
    shiftName: string;
    startTime: string;
    endTime: string;
    isClose: boolean;
    isBd: boolean;
}

export interface ExtendedShift extends Shift {
    assignedShifts: Shift;
    id: string;
}
export interface ShiftsByDate {
    [date: string]: ExtendedShift[];
}

export type UnavailableDay = {
    id: string;
    tenantStaffId: string;
    startDate: string;
    endDate: string;
    startTime: string;
    endTime: string;
    type: 'full-day' | 'time-off';
    reason: string | null;
    note: string;
    actionStatus: 'APPROVED' | 'PENDING' | 'REJECTED' | 'CANCELLED';
    tenantId: string;
    deletedBy: string | null;
    createdAt: string;
    updatedAt: string;
    deletedAt: string | null;
    tenant_id: string;
    tenant_staff_id: string;
    approved_tenant_staff_id: string | null;
    deleted_by: string | null;
};

export type AvailableTime = {
    to: string;
    from: string;
};

export type Event = {
    id: string;
    isRecurring: boolean;
    startDate: string;
    endDate: string;
    tenantId: string;
    tenantStaffId: string;
    approvedTenantStaffId: string | null;
    deletedBy: string | null;
    actionStatus: 'APPROVED' | 'PENDING' | 'REJECTED' | 'CANCELLED';
    note: string | null;
    type: 'full-day' | 'time-off';
    createdAt: string;
    updatedAt: string;
    deletedAt: string | null;
    dateRange: string;
    notes: string;
    weekday: string;
    isAllDay: boolean;
    isAvailable: boolean;
    availableTimes?: AvailableTime[];
};

export type Availability = {
    [key: string]: Event[];
};

export type Staff = {
    id: string;
    email: string | null;
    firstName: string;
    lastName: string;
    preferredName: string;
    signedUrl: string;
    photoThumbnail: string | null;
    jobRoles: JobRoleForShift[];
    shifts: ShiftsByDate;
    availabilities: Availability;
    unavailableDays: UnavailableDay[];
};

export type EventFormData = {
    id: string;
    guests: number;
    time: dayjs.Dayjs;
    desc: string;
    guestName: string;
};

export type StaffRequirement = {
    date: string; // ISO date string
    dayId: number;
    dayName: string;
    customCount: number | null;
};

export type CustomEvent = {
    date: string;
    dayId: number;
    dayName: string;
    eventData: EventFormData[]; // You can specify a more detailed type if you know the structure of the event data
};

export type ScheduleMetadata = {
    staffRequirement: StaffRequirement[];
    customEvents: CustomEvent[];
};

export type Schedule = {
    id: string;
    startDate: string;
    endDate: string;
    actionStatus: 'draft' | 'published' | '';
    publishedAt: string | null;
    scheduleMetadata: ScheduleMetadata;
};

export type ScheduleData = {
    holidays: Holidays[];
    mandatoryDays: MandatoryDay[];
    schedule: Schedule;
    staffs: Staff[];
};

export type SwapRequestStatus =
    | 'PENDING' // Owner
    | 'CANCELLED' // Owner
    | 'DELETED' // Owner
    | 'STAFF_APPROVED' // second user
    | 'STAFF_REJECTED' // second user
    | 'MANAGER_APPROVED' // Admin approval
    | 'MANAGER_REJECTED' // Admin rejection
    | 'POOL_REQUESTS' // View pool requests
    | 'CONFIRM_DELETE' // Confirm popup
    | 'TAKE_POOL_REQUEST'; // Take pool request

export type PoolData = {
    status: SwapRequestStatus;
    toShiftId: string;
    requestType: 'pool' | 'swap';
    swapRequestId: string;
};

export type AssignedShift = {
    shiftName: string;
    startTime: string;
    endTime: string;
    role: string;
    roleId: string;
    departmentId: string;
    isClose: boolean;
    isBd: boolean;
    isPoolRequested?: boolean;
    poolData?: PoolData;
    oldData?: PoolData;
};

export type SwapRequestDetails = {
    actionStatus: SwapRequestStatus;
    createdAt: string;
    fromShiftDetails: { fromEndTime: string; fromStartTime: string; fromRole: string; scheduleDate: string; shiftName: string };
    fromShiftId: string;
    fromTenantStaff: SwapStaffDetails;
    fromTenantStaffId: string;
    id: string;
    requestType: 'swap' | 'pool';
    scheduleId: string;
    tenantId: string;
    toShiftDetails: { toEndTime: string; toStartTime: string; toRole: string; scheduleDate: string; shiftName: string };
    toShiftId: string;
    toTenantStaff: SwapStaffDetails;
    toTenantStaffId: string;
    updatedAt: string;
};

export type UserDetails = {
    firstName: string;
    id: string;
    lastName: string;
    photoKey: string;
    preferredName: string;
};

export type SwapStaffDetails = Staff & {
    user: UserDetails;
};

export type PublishedSchedule = {
    scheduleId: string;
    scheduleStartDate: string;
    scheduleEndDate: string;
    id: string;
    tenantId: string;
    tenantStaffId: string;
    scheduleDate: string;
    assignedShifts: AssignedShift;
    createdAt: string;
    updatedAt: string;
    deletedAt: string | null;
    schedule_id: string;
    tenant_staff_id: string;
    tenant_id: string;
    fromSwapRequests: SwapRequestDetails[];
    toSwapRequests: SwapRequestDetails[];
};

type getDate = {
    selectedWeek: dayjs.Dayjs;
    day: string;
    format?: string;
};

export const getDateOfWeekday = ({ selectedWeek, day, format = 'MMM D, YYYY' }: getDate): string => {
    const normalizedWeekday = day.toLowerCase();
    let resultDate = selectedWeek.startOf('isoWeek');

    const daysOfWeek = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const targetDayIndex = daysOfWeek.indexOf(normalizedWeekday);

    while (resultDate.day() !== targetDayIndex) {
        resultDate = resultDate.add(1, 'day');
    }

    return resultDate.format(format);
};

// Deep clone the initial values to prevent unintended mutation
export const customDeepClone = (value: any): any => {
    if (dayjs.isDayjs(value)) {
        return dayjs(value); // or value.clone() depending on your needs
    }

    if (Array.isArray(value)) {
        return value.map(customDeepClone);
    }

    if (typeof value === 'object' && value !== null) {
        const clonedObject: Record<string, any> = {};
        for (const key in value) {
            if (Object.prototype.hasOwnProperty.call(value, key)) {
                clonedObject[key] = customDeepClone(value[key]);
            }
        }
        return clonedObject;
    }

    return value;
};

export const days: DayOfWeek[] = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

export const getFirstThreeLetter = (input: string | null) => {
    if (!input) return '';

    return input.charAt(0).toUpperCase() + input.slice(1, 3);
};

export const getFirstLetterUpperCase = (input: string): string => input?.charAt(0).toUpperCase();

export const getWeekRange = (selectedDate: dayjs.Dayjs) => {
    const startOfWeek = selectedDate.isoWeekday(1); // Start of the week (Monday)
    const endOfWeek = selectedDate.isoWeekday(7); // End of the week (Sunday)

    return {
        startDate: startOfWeek.format('YYYY-MM-DD'),
        endDate: endOfWeek.format('YYYY-MM-DD'),
    } as StartAndEndDates;
};

export type StartAndEndDates = {
    startDate: string;
    endDate: string;
};

export type ScheduleIdInfo = {
    id: string;
    startDate: string;
    endDate: string;
    actionStatus: 'draft' | 'published';
    publishedAt: null | string;
};

export type ScheduleIdsResponse = {
    draft: ScheduleIdInfo;
    published: ScheduleIdInfo;
};

export const fetchDraftedAndPublishedScheduleIds = (param: StartAndEndDates, xTenantId?: string) => {
    return HttpServiceHelper({
        url: `${scheduleApi}/all`,
        method: _HttpMethods.POST,
        data: param,
        xTenantId,
    });
};

export type ScheduleId = {
    scheduleId: string;
};

export const fetchStaffsWithSchedulesAndShift = ({ scheduleId }: ScheduleId, xTenantId?: string) => {
    return HttpServiceHelper({
        url: `${scheduleApi}/staffs_with_shifts/${scheduleId}`,
        method: _HttpMethods.POST,
        data: { flatDataWithUser: false, filterJobRoleAvailableForSchedule: true },
        xTenantId,
    });
};

export type ScheduleDepartmentDropDown = {
    depName: string;
    departmentId: string;
};

export type StyleDetails = {
    backgroundColor: string; // Hex color code
    color: string | undefined; // Hex color code
};

// payload for fetching user roles
export const getUserRoles = (xTenantId?: string) => {
    return HttpServiceHelper({
        url: `${userRolesApi}?limit=500`,
        method: _HttpMethods.GET,
        xTenantId,
    });
};

export function isDateInSelectedWeek({
    startDate,
    endDate,
    selectedWeek,
}: {
    startDate: string;
    endDate: string;
    selectedWeek: dayjs.Dayjs;
}): boolean {
    const start = dayjs(startDate);
    const end = dayjs(endDate);

    const weekStart = selectedWeek.startOf('week'); // Assumes week starts on Sunday
    const weekEnd = selectedWeek.endOf('week'); // Ends on Saturday

    const isStartWithinWeek = start.isBetween(weekStart, weekEnd, 'day', '[]');
    const isEndWithinWeek = end.isBetween(weekStart, weekEnd, 'day', '[]');

    return isStartWithinWeek && isEndWithinWeek;
}

export const getStaffAvailabilityForTheDay = ({
    day,
    availabilities,
    selectedWeek,
    type = 'others',
}: {
    day: string;
    availabilities: Availability | undefined;
    selectedWeek: dayjs.Dayjs;
    type?: 'schedule' | 'others';
}) => {
    // Check if availabilities exist for the specified day
    if (!availabilities || Object.keys(availabilities).length === 0) {
        return null;
    }

    const staffAvailabilities: any = availabilities[day]?.filter((availability) => {
        const { weekday, startDate, endDate } = availability;
        const isDateCorrect = isDateInSelectedWeek({ startDate, endDate, selectedWeek });

        // return isAvailable && day === weekday && isDateCorrect;
        return (
            day === weekday &&
            (isDateCorrect || availability.isRecurring === true) &&
            (availability.actionStatus === 'APPROVED' || availability.actionStatus === 'PENDING')
        );
    });

    let sortedAvailabilities: any[] = [];
    if (staffAvailabilities) {
        const hasNonRecurring = staffAvailabilities.some((availability: any) => !availability.isRecurring);

        // if there is temporary availability, use it
        const filteredAvailabilities = hasNonRecurring
            ? staffAvailabilities.filter((availability: any) => !availability.isRecurring)
            : staffAvailabilities.filter((availability: any) => availability.isRecurring);

        // Now sort the filtered list by `createdAt` in descending order
        sortedAvailabilities = filteredAvailabilities.sort(
            (a: any, b: any) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );
    }
    const staffAvailability = sortedAvailabilities.slice(0, 1);

    // if no availability, return null (Not Set)
    if (staffAvailability?.length === 0) {
        return null;
    }

    if (!staffAvailability[0]?.isAvailable) {
        if (type === 'schedule') {
            return (
                // <CustomTooltip
                // 	staffName={preferredName}
                // 	showDelay={500}
                // 	content={timeOffText}
                // >
                <div className="scheduler-shift-card-body-not-available">
                    <div className="d-flex">
                        <div className="flex-grow-0 d-flex align-items-center justify-center">
                            <span className="shift-role">
                                <i className="ri-time-line"></i>
                            </span>
                        </div>
                        <div className="flex-grow-1 shift-name-main text-nowrap">
                            <strong>N/A</strong>
                        </div>
                    </div>
                </div>
                // </CustomTooltip>
            );
        }
        if (type === 'others') {
            return <strong>N/A</strong>;
        }
    }
};

export function isDateInRange({ startDate, endDate, date }: { startDate: string; endDate: string; date: string }): boolean {
    // Parse the dates using Moment.js
    const targetDate = moment(date, 'YYYY-MM-DD');
    const start = moment(startDate, 'YYYY-MM-DD');
    const end = moment(endDate, 'YYYY-MM-DD');

    // Check if the date is within the range (inclusive)
    return targetDate.isBetween(start, end, undefined, '[]');
}

export const formatTimeToAmPm = (timeString: string) => dayjs(timeString, 'HH:mm:ss').format('hh:mm A');

export const getTimeOffMessage = (
    {
        unavailability,
        type = 'others',
        actionButton = false,
    }: {
        unavailability: UnavailableDay[];
        type?: 'schedule' | 'others';
        actionButton?: boolean;
    },
    date: string
) => {
    // Filter unavailability based on the matching date
    const filteredDays = unavailability.filter((timeOff) => {
        const currentDate = dayjs(date, 'YYYY-MM-DD');
        const startDate = dayjs(timeOff?.startDate, 'YYYY-MM-DD');
        const endDate = dayjs(timeOff?.endDate, 'YYYY-MM-DD');
        if (
            currentDate.isBetween(startDate, endDate, 'day') ||
            currentDate.isSame(startDate, 'day') ||
            currentDate.isSame(endDate, 'day')
        ) {
            return true;
        }
        return false;
        // return timeOff.startDate === date || timeOff.endDate === date
    });

    // console.log({filteredDays})
    const wrapTemplate = (children: JSX.Element, timeOff: UnavailableDay) => {
        if (type === 'others') {
            return (
                <div className="scheduler-shift-card-body-not-available">
                    <div className="d-flex">
                        <div className="flex-grow-0 d-flex align-items-center justify-center">
                            <span className="shift-role">
                                <i className="ri-time-line"></i>
                            </span>
                        </div>
                        <div className="flex-grow-1 shift-name-main text-nowrap">{children}</div>
                    </div>
                </div>
            );
        }
        if (type === 'schedule') {
            const acceptRequest = (event: React.MouseEvent<HTMLButtonElement>) => {
                confirmDialog({
                    className: 'custom-confirm-dialog',
                    message: 'Are you sure you want to accept this request?',
                    icon: 'pi pi-info-circle',
                    acceptClassName: 'p-button-success',
                    rejectClassName: 'p-button-danger',
                    draggable: false,
                    accept: () => {
                        // TODO: call API to accept request
                    },
                });
            };

            const rejectRequest = (event: React.MouseEvent<HTMLButtonElement>) => {
                confirmDialog({
                    className: 'custom-confirm-dialog',
                    message: 'Are you sure you want to reject this request?',
                    icon: 'pi pi-info-circle',
                    draggable: false,
                    acceptClassName: 'p-button-success',
                    rejectClassName: 'p-button-danger',
                    accept: () => {
                        // TODO: call API to reject request
                    },
                });
            };

            const checkIfMultipleDays = timeOff?.startDate !== timeOff?.endDate;
            const toolTipContent = (
                <div className="d-flex flex-column" style={{ minWidth: '270px' }}>
                    <div className="d-flex flex-row justify-content-between my-2">
                        <div className="flex-grow-1">
                            <strong>Requested Time off</strong>
                        </div>
                        {timeOff?.actionStatus === 'PENDING' && (
                            <div className="flex-grow-0">
                                <Badge className="custom-badge" value={'Pending'} severity="warning" />
                            </div>
                        )}
                    </div>
                    <div className="mt-2 d-flex flex-row align-items-center">
                        {checkIfMultipleDays ? (
                            <strong>
                                {`${dayjs(timeOff?.startDate).format(DATE_FORMAT)} - ${dayjs(timeOff?.endDate).format(DATE_FORMAT)}`}
                            </strong>
                        ) : (
                            <strong>
                                {timeOff?.startTime && timeOff?.endTime
                                    ? `${dayjs(timeOff?.startDate).format(DATE_FORMAT)} | ${formatTimeToAmPm(timeOff?.startTime)} to ${formatTimeToAmPm(timeOff?.endTime)}`
                                    : `${dayjs(timeOff?.startDate).format(DATE_FORMAT)}`}
                            </strong>
                        )}
                    </div>
                    <hr />
                    <div className="mt-2">{timeOff?.reason ?? ''}</div>
                    {timeOff?.actionStatus === 'PENDING' && actionButton && (
                        <>
                            <hr />
                            <ConfirmDialog />
                            <div className="mt-2 d-flex align-items-center justify-content-end">
                                <button className="btn btn-sm btn-danger" type="button" onClick={rejectRequest}>
                                    Reject
                                </button>
                                <button className="ms-2 btn btn-sm btn-success" type="button" onClick={acceptRequest}>
                                    Approve
                                </button>
                            </div>
                        </>
                    )}
                </div>
            );
            return (
                <CustomTooltip
                    key={'pending-shift-schedule-' + timeOff?.id}
                    staffName={null}
                    showDelay={500}
                    content={toolTipContent}
                    manualClose={timeOff?.actionStatus === 'PENDING'}
                >
                    <div className="scheduler-shift-card-body-not-available">
                        <div className="d-flex">
                            <div className="flex-grow-0 d-flex align-items-center justify-center">
                                <span className="shift-role">
                                    <i className="ri-time-line"></i>
                                </span>
                            </div>
                            <div className="flex-grow-1 shift-name-main text-nowrap">{children}</div>
                        </div>
                    </div>
                </CustomTooltip>
            );
        }
    };

    // Return the mapped JSX for the filtered days
    return filteredDays.map((timeOff, index) => {
        if (timeOff.actionStatus === 'APPROVED') {
            if (timeOff.type === 'full-day') {
                return wrapTemplate(
                    <div key={index}>
                        <span style={{ marginRight: '3px', borderBottom: 'none' }}>
                            <strong>R/O:</strong>
                        </span>
                        <span style={{ color: 'red', borderBottom: 'none' }}>Full Day</span>
                    </div>,
                    timeOff
                );
            } else {
                return wrapTemplate(
                    <div key={index}>
                        <span style={{ marginRight: '3px', borderBottom: 'none' }}>
                            <strong>R/O:</strong>
                        </span>
                        <span style={{ color: 'red', borderBottom: 'none' }}>
                            {`${formatTimeToAmPm(timeOff.startTime)} to ${formatTimeToAmPm(timeOff.endTime)}`}
                        </span>
                    </div>,
                    timeOff
                );
            }
        } else if (timeOff.actionStatus === 'PENDING') {
            return wrapTemplate(
                <div key={index}>
                    <span style={{ marginRight: '3px', borderBottom: 'none' }}>
                        <strong>R/O:</strong>
                    </span>
                    <span style={{ color: '#fbaa07', borderBottom: 'none' }}>Pending</span>
                </div>,
                timeOff
            );
        }
    });
};

// check if the shift comes under am/pm
export const checkIfShiftAm = (startTime: string) => {
    const parsedStartTime = dayjs(startTime, 'HH:mm A');
    const isAm = parsedStartTime.isBefore(dayjs('12:00 PM', 'HH:mm A'));
    return isAm;
};

export type PrimeReactDropDown = {
    name: string;
    code: string;
};

export type Users = {
    jobRoles: JobRole[];
    createdAt: number;
    deletedAt: string | null;
    email: string;
    preferredName: string;
    firstName: string;
    lastName: string;
    loginUserName: string;
    hireDate: string;
    id: string;
    phoneNumber: string;
    photoKey: string | null;
    photoKeyThumbnail: string | null;
    picture: string;
    posNumber: string;
    signedUrl: string | undefined;
    isActive: boolean;
    userType: string;
    passwordLessLoginOnly: boolean;
    sendPasswordCreation: boolean;
    preferredLangKey: string;

    activeTenants: Tenants[];
    staffPermissionRoles: RolePermissions[];
    isAdmin: boolean;
    tenantId?: string;
    tenantDetails?: {
        tenantId: string;
        tenantName: string;
    };
};

export type Tenants = {
    id: string;
    tenantName: string;
};
export type RolePermissions = {
    id: string;
    permissionRoleName: string;
    permissionRoleDescription: string;
    permissionKey: string;
    isActive: boolean;
    isDefault: boolean;
    tenantId: string;
    permissions: Permission[];
    permissionRoleId?: string;
};

export type Permission = {
    id: string;
    parentPermissionId: string | null;
    permissionId: string | null;
    moduleId: string;
    permissionKey: string;
    permissionName: string;
    permissionDescription?: string;
    isActive: boolean;
    isDefault: boolean;
    permissions: Permission[];
};

// fetch all users
export const getUsersList = (urlParams: string, xTenantId?: string) => {
    return HttpServiceHelper({
        url: usersApi + urlParams,
        method: _HttpMethods.GET,
        xTenantId,
    });
};

// slim staff row from /organization/venue/staff/options; id matches staff.id in staffs_with_shifts
export type _VenueStaffOption = {
    id: string;
    preferredName: string;
    isActive: boolean;
    jobRoles?: { isAvailableForSchedule?: boolean }[];
};

// the endpoint pages at 10 by default, so ask for every staff member of the venue
const VENUE_STAFF_OPTIONS_LIMIT = 500;

// staff picker list for a venue (Schedules users filter)
export const getVenueStaffOptions = (xTenantId?: string) => {
    return HttpServiceHelper({
        url: `${venueStaffOptionsApi}?limit=${VENUE_STAFF_OPTIONS_LIMIT}`,
        method: _HttpMethods.GET,
        xTenantId,
    });
};

export interface EmployeeScheduleData {
    preferredName: string;
    staffName: string;
    staffId: string;
    staffData: Staff;
    roles: string[];
    staffRequirement: StaffRequirement[];
    monday: Shift[];
    tuesday: Shift[];
    wednesday: Shift[];
    thursday: Shift[];
    friday: Shift[];
    saturday: Shift[];
    sunday: Shift[];
    customEvents: CustomEvent[] | [];
}
